import React, { useRef, useState } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa6';
import Select from 'react-select';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useClearReportFilterParams,
    useReportFilters,
} from '~/context/ReportFiltersProvider';
import ResetFiltersButton from './ResetFiltersButton';

export type AccordionItem = {
    id: number;
    active: number | null;
    handleToggle: (index: number) => void;
};

interface SelectOption {
    value: string;
    label: string;
}

const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

const getDatePresets = () => {
    const today = new Date();

    return [
        {
            value: '',
            label: 'All time',
            getRange: () => ({ start: '', end: '' }),
        },
        {
            value: 'last_30_days',
            label: '1 Month',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 30);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_6_months',
            label: '6 Months',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setMonth(start.getMonth() - 6);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'ytd',
            label: 'YTD',
            getRange: () => {
                const start = new Date(today.getFullYear(), 0, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_1_year',
            label: '1 Year',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setFullYear(start.getFullYear() - 1);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'current_fiscal_year',
            label: 'Current Fiscal Year',
            getRange: () => {
                const fyStartYear =
                    today.getMonth() >= 9
                        ? today.getFullYear()
                        : today.getFullYear() - 1;
                const start = new Date(fyStartYear, 9, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'custom',
            label: 'Custom',
            getRange: () => ({ start: '', end: '' }),
        },
    ];
};

const timeScaleOptions: SelectOption[] = [
    { value: '', label: 'All time' },
    { value: 'last_30_days', label: '1 Month' },
    { value: 'last_6_months', label: '6 Months' },
    { value: 'ytd', label: 'YTD' },
    { value: 'last_1_year', label: '1 Year' },
    { value: 'current_fiscal_year', label: 'Current Fiscal Year' },
    { value: 'custom', label: 'Custom' },
];

const StatusDurationAccordionItem: React.FC<AccordionItem> = ({
    active,
    handleToggle,
    id,
}) => {
    const contentEl = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setFilterParams, setActiveAccordion } = useReportFilters();
    const clearReportFilterParams = useClearReportFilterParams();

    const datePresets = getDatePresets();

    const [submittedStartDate, setSubmittedStartDate] = useState(
        searchParams.get('submitted_date_start') || '',
    );
    const [submittedEndDate, setSubmittedEndDate] = useState(
        searchParams.get('submitted_date_end') || '',
    );
    const [selectedTimeScale, setSelectedTimeScale] = useState('');

    const hasActiveFilters =
        submittedStartDate !== '' || submittedEndDate !== '';

    const handleResetFilters = () => {
        setSubmittedStartDate('');
        setSubmittedEndDate('');
        setSelectedTimeScale('');
        clearReportFilterParams();
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const params = new URLSearchParams();
        if (submittedStartDate) {
            params.append('submitted_date_start', submittedStartDate);
        }
        if (submittedEndDate) {
            params.append('submitted_date_end', submittedEndDate);
        }

        setFilterParams(params.toString());
        setActiveAccordion(5);
        navigate(`/reports/status-duration?${params.toString()}`);
    };

    return (
        <div
            className={`w-full max-w-[932px] rounded-sm border bg-white px-7.5 py-4 shadow-default ${
                active === id ? 'border-primary' : 'border-stroke'
            }`}
        >
            <button
                className={`flex w-full items-center justify-between gap-2 ${
                    active === id ? 'active' : ''
                }`}
                onClick={() => handleToggle(id)}
            >
                <div>
                    <h4 className='text-title-xsm font-bold sm:text-title-md'>
                        Fund Request Status Duration
                    </h4>
                </div>
                <div className='flex items-center gap-2'>
                    <FaPlus className={`${active === id ? 'hidden' : ''}`} />
                    <FaMinus
                        className={`${active === id ? 'block' : 'hidden'}`}
                    />
                </div>
            </button>

            <div
                ref={contentEl}
                className={`py-5 duration-200 ease-in-out ${
                    active === id ? 'block' : 'hidden'
                }`}
            >
                <form onSubmit={handleSearch}>
                    <div className='flex flex-wrap gap-[30px] lg:flex-nowrap'>
                        <div className='w-full lg:w-3/5'>
                            <h2 className='text-[#949599] text-[18px/22px]'>
                                FILTERS
                            </h2>

                            {/* Time Scale Preset */}
                            <div className='mt-4'>
                                <label
                                    htmlFor='sd-time-scale'
                                    className='block text-[12px]/[14px] font-extrabold'
                                >
                                    Date Submitted
                                </label>
                                <Select
                                    id='sd-time-scale'
                                    menuPlacement='auto'
                                    options={timeScaleOptions}
                                    isClearable
                                    value={
                                        timeScaleOptions.find(
                                            (opt) =>
                                                opt.value === selectedTimeScale,
                                        ) || null
                                    }
                                    onChange={(option) => {
                                        const presetValue = option?.value || '';
                                        setSelectedTimeScale(presetValue);
                                        const preset = datePresets.find(
                                            (p) => p.value === presetValue,
                                        );
                                        if (
                                            preset &&
                                            presetValue !== 'custom'
                                        ) {
                                            const range = preset.getRange();
                                            setSubmittedStartDate(range.start);
                                            setSubmittedEndDate(range.end);
                                        }
                                    }}
                                    placeholder='All time'
                                    classNames={{
                                        control: () =>
                                            'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
                                        placeholder: () => 'text-[#949599]',
                                    }}
                                />
                            </div>

                            {/* Custom Date Range */}
                            <div className='mt-4'>
                                <label className='mb-1 block text-[12px]/[14px] font-extrabold'>
                                    Date Range
                                </label>
                                <div className='flex items-center gap-2'>
                                    <input
                                        type='date'
                                        value={submittedStartDate}
                                        onChange={(e) => {
                                            setSubmittedStartDate(
                                                e.target.value,
                                            );
                                            setSelectedTimeScale('custom');
                                        }}
                                        className='rounded-[4px] border border-[#949599] p-[10px]'
                                    />
                                    <span className='text-gray-500'>to</span>
                                    <input
                                        type='date'
                                        value={submittedEndDate}
                                        onChange={(e) => {
                                            setSubmittedEndDate(e.target.value);
                                            setSelectedTimeScale('custom');
                                        }}
                                        className='rounded-[4px] border border-[#949599] p-[10px]'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='w-full lg:w-2/5'>
                            <div className='w-full rounded bg-[#F4F4F5] p-5'>
                                <h2 className='text-[#949599] text-[18px/22px]'>
                                    ABOUT THIS REPORT
                                </h2>
                                <p className='mt-3 text-sm text-gray-600'>
                                    Generate a chronographic report of how long
                                    each fund request spent in each status. Each
                                    row represents a single status visit.
                                </p>
                                <p className='mt-3 text-sm text-gray-600'>
                                    Filter by the date the fund request was
                                    first submitted. Results can be exported to
                                    CSV.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className='mt-8 flex flex-wrap justify-center gap-4'>
                        <ResetFiltersButton
                            onReset={handleResetFilters}
                            disabled={!hasActiveFilters}
                        />
                        <button
                            type='submit'
                            className='rounded-lg bg-primary px-8 py-3 text-center text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50'
                        >
                            Generate Report
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StatusDurationAccordionItem;
